// src/components/openDialog/CreateRelaxationDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Upload as UploadIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const CATEGORIES = [
  'Nature Sounds',
  'Guided Meditation',
  'Ambient Music',
  'Sound Healing',
  'White Noise',
  'Binaural Beats',
  'ASMR',
  'Sleep Story',
  'Yoga & Relaxation',
  'Other',
];

const CreateRelaxationDialog = ({
  open,
  onClose,
  onSave,
  courseId,
  initialValues = null,     // ← added support for prefill from recording
}) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    duration: '',
    instructions: '',
    benefits: '',
    audioFile: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Apply pre-filled values (from recording or edit mode)
  useEffect(() => {
    if (open && initialValues) {
      setForm({
        name: initialValues.name || '',
        description: initialValues.description || '',
        category: initialValues.category || '',
        duration: initialValues.duration || '',
        instructions: initialValues.instructions || '',
        benefits: initialValues.benefits ? initialValues.benefits.join('\n') : '',
        audioFile: initialValues.audioFile || null,
      });
    }
  }, [open, initialValues]);

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      category: '',
      duration: '',
      instructions: '',
      benefits: '',
      audioFile: null,
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setForm((prev) => ({ ...prev, audioFile: file }));

    if (file.type.startsWith('audio/')) {
      const audio = new Audio(URL.createObjectURL(file));
      audio.onloadedmetadata = () => {
        const min = Math.floor(audio.duration / 60);
        const sec = Math.floor(audio.duration % 60);
        setForm((prev) => ({
          ...prev,
          duration: `${min}:${sec.toString().padStart(2, '0')}`,
        }));
      };
    }
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('Track name is required');
      return;
    }
    if (!form.category) {
      toast.error('Please select a category');
      return;
    }
    if (!form.audioFile) {
      toast.error('Audio file is required');
      return;
    }

    setIsSubmitting(true);

    // Prepare payload – send actual File object for FormData upload
    const payload = {
      trackName: form.name.trim(),
      shortDescription: form.description.trim(),
      category: form.category,
      duration: form.duration || '',
      instructions: form.instructions.trim(),
      benefits: form.benefits
        ? form.benefits
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
        : [],
      audioFile: form.audioFile,           // ← File object (important!)
      courseId: Number(courseId),          // if backend needs it here
    };

    console.log('Submitting:', payload);

    onSave(payload);

    // Reset only after successful save (better UX)
    // You can also move reset to onSuccess callback in parent
    setIsSubmitting(false);
    resetForm();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Create New Relaxation
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* 1. Track Name */}
          <TextField
            autoFocus
            required
            fullWidth
            label="Track Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="e.g. Evening Forest Rain, 10-Min Guided Calm"
          />

          {/* 2. Category */}
          <FormControl fullWidth required>
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={form.category}
              label="Category"
              onChange={handleChange}
            >
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 3. Duration */}
          <TextField
            fullWidth
            label="Duration"
            name="duration"
            value={form.duration}
            onChange={handleChange}
            placeholder="10:30"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccessTimeIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            helperText="Auto-detected from audio file (mm:ss)"
          />

          {/* 4. Audio File + Preview */}
          <Box>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Audio File <span style={{ color: 'red' }}>*</span>
            </Typography>

            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                py: 2.5,
                borderStyle: form.audioFile ? 'solid' : 'dashed',
                borderColor: form.audioFile ? 'primary.main' : 'grey.400',
              }}
            >
              {form.audioFile
                ? `Selected: ${form.audioFile.name}`
                : 'Choose Audio File (mp3, wav, webm, m4a...)'}
              <input
                type="file"
                hidden
                onChange={handleFileChange}
              />
            </Button>

            {form.audioFile && (
              <Box sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
                <audio
                  controls
                  src={URL.createObjectURL(form.audioFile)}
                  style={{ width: '100%', height: 48 }}
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* 5. Short Description */}
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Short Description (optional)"
            name="description"
            value={form.description}
            onChange={handleChange}
            placeholder="Gentle rain with distant thunder — ideal for sleep and stress relief"
          />

          {/* 6. Instructions / Script */}
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Instructions or Guided Script (optional)"
            name="instructions"
            value={form.instructions}
            onChange={handleChange}
            placeholder="1. Find a quiet space\n2. Lie down comfortably\n3. Close your eyes...\n\nYou can paste your full meditation script here."
          />

          {/* 7. Benefits */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Benefits (one per line, optional)"
            name="benefits"
            value={form.benefits}
            onChange={handleChange}
            placeholder="• Deep relaxation\n• Helps fall asleep faster\n• Reduces anxiety\n• Improves focus"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !form.name.trim() || !form.category || !form.audioFile}
        >
          {isSubmitting ? 'Creating...' : 'Create Relaxation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRelaxationDialog;